import React, { useEffect, useRef } from 'react';
import { PieChart as PieChartChartist, type PieChartOptions } from 'chartist';
import styles from './style.module.scss';
import type { PropsWithTestId } from 'types';

//#region Constants
const DEFAULT_WIDTH = 260;
const DEFAULT_HEIGHT = 260;

const DEFAULT_OPTIONS: ChartOptions = {
  showLabel: false,
  width: DEFAULT_WIDTH,
  height: DEFAULT_HEIGHT,
};

//#region Types
type DataPoint = { percentage: number };
type ChartOptions = Required<Pick<PieChartOptions, 'showLabel' | 'width' | 'height'>>;
type PieChartProps = PropsWithTestId<{
  data: DataPoint[];
  options?: ChartOptions;
}>;
//#endregion

//#region Component
export default function PieChart({ data, testId, options = DEFAULT_OPTIONS }: PieChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<PieChartChartist | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    // Clean up previous chart instance
    if (chartInstanceRef.current) {
      chartInstanceRef.current.detach();
      chartInstanceRef.current = null;
    }

    try {
      const chart = new PieChartChartist(ref.current, {
        series: data.map((item, index) => ({
          value: item.percentage,
          className: styles[`section-${index}`],
        })),
      }, options);

      chartInstanceRef.current = chart;
    } catch (e: unknown) {
      console.error(`Error creating pie chart: ${e}`);
    }
  }, [data, options]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.detach();
      }
    };
  }, []);

  return <div ref={ref} data-testid={testId} />;
}
